import { EmptyState, SkeletonLoader } from '@payaca/ambrosia';
import { useObserveIntersection } from '@payaca/hooks/useObserveIntersection';
import { FC, useMemo } from 'react';

import {
  useOptimisticArchiveInboundEmail,
  useOptimisticUnarchiveInboundEmail,
} from '@/api/mutations/inbound-emails/optimistic';
import useGetInboundEmailInbox from '@/api/queries/inbound-emails/useGetInboundEmailInbox';

import EmailCard from './EmailCard';

export type InboxEmailListProps = {
  inboxResult: ReturnType<typeof useGetInboundEmailInbox>;
  selectedEmailId: string | null;
  onSelectEmail: (emailId: string | null) => void;
};

const InboxEmailList: FC<InboxEmailListProps> = (props) => {
  const { inboxResult, selectedEmailId, onSelectEmail } = props;

  const { mutate: archiveEmail } = useOptimisticArchiveInboundEmail();
  const { mutate: unarchiveEmail } = useOptimisticUnarchiveInboundEmail();

  const allEmails = useMemo(() => {
    return (
      inboxResult.data?.pages.flatMap(
        (page) => page.me.inboundEmailInbox.items
      ) ?? []
    );
  }, [inboxResult.data]);

  const loaderRef = useObserveIntersection(
    () => inboxResult.hasNextPage && void inboxResult.fetchNextPage()
  );

  return (
    <div className="flex-1 space-y-2.5 pt-0">
      {allEmails.length === 0 && !inboxResult.isLoading && (
        <EmptyState
          className="mt-8"
          text="No emails found"
          iconName="mail-01.3"
        />
      )}

      {allEmails.map((email) => (
        <EmailCard
          key={email.id}
          email={email}
          isSelected={selectedEmailId === email.id}
          onSelect={() => onSelectEmail(email.id)}
          onArchive={
            email.status !== 'ARCHIVED'
              ? () => archiveEmail({ inboundEmailId: email.id })
              : undefined
          }
          onUnarchive={
            email.status === 'ARCHIVED'
              ? () => unarchiveEmail({ inboundEmailId: email.id })
              : undefined
          }
        />
      ))}

      {inboxResult.hasNextPage && (
        <div ref={loaderRef}>
          <SkeletonLoader.Notification />
        </div>
      )}

      {inboxResult.isLoading && (
        <div className="space-y-2 p-4">
          <SkeletonLoader.Notification />
          <SkeletonLoader.Notification />
          <SkeletonLoader.Notification />
        </div>
      )}
    </div>
  );
};

export default InboxEmailList;
